import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { BookOpen, Calendar, ArrowRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <Loader2 className="animate-spin text-terracotta" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-sand-light" data-testid="blog-page">
      <Helmet>
        <title>Blog | Hilanta - Conseils Bien-être & Massages Marseille</title>
        <meta name="description" content="Découvrez nos articles sur le bien-être, les massages et la santé naturelle. Conseils et astuces par Hilanta Marseille." />
      </Helmet>
      
      {/* Header */}
      <section className="py-8 sm:py-12 bg-sand">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="mx-auto text-terracotta mb-4" size={40} />
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-warm-brown mb-4">
            Notre Blog
          </h1>
          <p className="font-body text-sm sm:text-base text-warm-brown-light max-w-2xl mx-auto">
            Conseils bien-être, astuces santé et actualités de Hilanta
          </p>
        </div>
      </section>

      {/* Posts */}
      <section className="py-8 sm:py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {posts.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="mx-auto text-warm-brown-light mb-4" size={48} />
              <p className="font-body text-warm-brown-light text-lg">
                Aucun article pour le moment.
              </p>
              <p className="font-body text-warm-brown-light text-sm mt-2">
                Revenez bientôt pour découvrir nos conseils bien-être !
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-shadow"
                  data-testid={`blog-post-${post.id}`}
                >
                  {/* Image */}
                  {post.image_url ? (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-terracotta/20 to-sage/20 flex items-center justify-center">
                      <BookOpen className="text-terracotta/50" size={48} />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-warm-brown-light text-xs mb-3">
                      <Calendar size={14} />
                      <span>{formatDate(post.created_at)}</span>
                    </div>
                    
                    <h2 className="font-heading text-xl text-warm-brown mb-2 group-hover:text-terracotta transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    
                    <p className="font-body text-sm text-warm-brown-light line-clamp-3 mb-4">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center gap-2 text-terracotta font-body text-sm group-hover:gap-3 transition-all">
                      <span>Lire la suite</span>
                      <ArrowRight size={16} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
